<script setup lang="ts">
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { getMomentsFromChapter, useStory } from "@/stores/story";
import { computed, onBeforeMount, ref, watch } from "vue";
import { Mate, Media } from "@/types/entity";
import { useRoute, useRouter } from "vue-router";
import {
  Image,
  Popup,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
  Tab,
  Tabs,
} from "vant";
import StoryView from "@/components/mobile/story/story-chapter/StoryView.vue";
import { useMate } from "@/stores/mate";
import StoryInfoEdit, {
  StoryInfoEditExpose,
} from "@/components/mobile/story/story-chapter/StoryInfoEdit.vue";
import ThreadItem from "@/components/mobile/ThreadItem.vue";
import DeleteIcon from "@/components/icon/deleteIcon.vue";
import EditIcon from "@/components/icon/editIcon.vue";
import StoryShareButton from "@/components/mobile/story/story-chapter/StoryShareButton.vue";
import StoryCard from "@/components/mobile/story/story-chapter/StoryCard.vue";
import DownloadIcon from "@/components/icon/DownloadIcon.vue";
import StoryChapterPublishPopup from "@/components/mobile/story/story-chapter/StoryChapterPublishPopup.vue";
import { extractImgUrlList } from "@/modules/editor/editor-json-utils";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import { isImage } from "@/modules/common";
import VideoIcon from "@/components/icon/VideoIcon.vue";
import MateMediaPreview from "@/components/mobile/overlay/MateMediaPreview.vue";
import i18n from "@/locales";

const route = useRoute();
const router = useRouter();
const storyStore = useStory();
const mateStore = useMate();
const currentStory = computed(() => {
  if (!route.params.uuid) {
    return null;
  }
  return storyStore.storyMap[route.params.uuid as string];
});
const relatedMomentList = computed(() => {
  if (!currentStory.value || !currentStory.value!.storyChapterzz) {
    return [];
  }
  // moment排序去重
  return getMomentsFromChapter(currentStory.value!.storyChapterzz).sort(
    (a, b) => new Date(b.dtCreate).getTime() - new Date(a.dtCreate).getTime()
  );
});
const loading = ref(false);
const currentTab = ref(0);
const author = ref<Mate | null>(null);
const isOwner = computed(() => {
  return (
    currentStory.value &&
    currentStory.value.mate.id === mateStore.currentMate!.id
  );
});
const canEdit = computed(() => isOwner.value && !currentStory.value!.dtPublish);

const showEditPopup = ref(false);
const showPublishPopup = ref(false);
const showStoryShare = ref(false);

const reactionCount = computed(() => {
  if (!currentStory.value) {
    return 0;
  }
  return currentStory.value.reply.interactivityEmoji.reduce(
    (acc, cur) => acc + cur.amount,
    0
  );
});
const mediaList = computed(() => {
  if (!currentStory.value) return [];
  const mediaMap = extractImgUrlList(
    storyStore.getJson(currentStory.value!.text)
  ).reduce((acc, cur) => {
    if (!acc.has(cur)) {
      acc.set(cur, cur);
    }
    return acc;
  }, new Map());
  return Array.from(mediaMap.values());
});
const showPreview = ref(false);
const startPosition = ref(0);
const previewMediaList = computed(() => {
  return mediaList.value.map((url) => ({ url } as Media));
});
// Lifecycle
const prepareData = async () => {
  loading.value = true;
  try {
    const uuid: string = route.params.uuid as string;
    await storyStore.getStoryItem(uuid, true);
    // Story.text若为plainText，则转换为JSON并将Moment中的图片插入到JSON中
    author.value = await mateStore.getOrFetchMate(
      currentStory.value!.mate.uuid
    );
  } finally {
    loading.value = false;
  }
};
onBeforeMount(() => {
  prepareData();
});
const storyInfoEditRef = ref<StoryInfoEditExpose | null>(null);
watch(showEditPopup, (show) => {
  if (show) {
    storyInfoEditRef.value?.reset();
  }
});
// EventHandler
const onStoryEditSuccess = () => {
  showEditPopup.value = false;
};
const onStoryDelete = async () => {
  try {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.deleteChapter"),
      message: i18n.global.t("ConfirmModal.deleteCurrentChatInfo"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    const loadingToast = showLoadingToast({
      message: i18n.global.t("Message.deletingChapter"),
      duration: 0,
    });
    try {
      await storyStore.deleteStory(currentStory.value!);
      loadingToast.close();
      showSuccessToast(i18n.global.t("Message.deleteChapterSuccessful"));
      router.back();
    } finally {
      loadingToast.close();
    }
  } catch {
    // Cancel => Do nothing
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] relative story-home">
    <threads-nav :show-back="true" :enable-switch-mate="false">
      <template #center>
        <div></div>
      </template>
      <template #right>
        <button
          v-if="canEdit"
          class="btn btn-primary btn-sm rounded"
          @click="showPublishPopup = true"
        >
          {{ $t("Buttons.publish") }}
        </button>
      </template>
    </threads-nav>
    <div class="w-full h-full overflow-y-auto flex flex-col">
      <div
        v-if="loading"
        class="w-full h-full flex items-center justify-center"
      >
        <span class="loading"></span>
      </div>
      <div v-else>
        <story-card
          :story-book="currentStory!"
          :cover-url="currentStory!.media?.url"
          :title="currentStory!.title"
          :desc="currentStory?.description"
          :mate="author!"
          :view-count="currentStory?.reply.viewerCount"
          :reaction-count="reactionCount"
          :reply-count-count="currentStory?.reply.replyCount"
          :dt-publish="currentStory!.dtPublish"
          @on-card-click="
            router.push({
              name: 'storyInfo',
              params: { uuid: currentStory!.uuid },
            })
          "
        >
          <template #button-group>
            <reaction-counter :reply="currentStory?.reply" />
            <story-share-button
              v-if="isOwner"
              v-model:show="showStoryShare"
              :story="currentStory!"
            >
              <button class="btn btn-xs btn-neutral rounded">
                <download-icon class="icon-sm" />
              </button>
            </story-share-button>

            <button
              v-show="isOwner"
              class="btn btn-xs btn-neutral rounded"
              @click="showEditPopup = true"
            >
              <edit-icon class="icon-sm" />
            </button>
            <button
              class="text-base-content/50 btn btn-xs btn-neutral rounded"
              @click="
                $router.push({
                  name: 'storyImgModule',
                  query: { story: currentStory!.uuid },
                })
              "
            >
              <i class="iconfont icon-story font-light text-white"></i>
            </button>
            <button
              v-show="isOwner"
              class="btn btn-xs btn-neutral rounded"
              @click="onStoryDelete"
            >
              <delete-icon class="icon-sm" />
            </button>
          </template>
        </story-card>
        <div>
          <tabs
            v-model:active="currentTab"
            swipeable
            class="h-full thread-tabs"
            sticky
            offset-top="56"
          >
            <tab :title="$t('story.tab.content')">
              <div v-if="!(currentStory!.text)">
                <div
                  class="flex flex-col items-center justify-center h-[200px]"
                >
                  <div class="flex flex-col items-center justify-center gap-2">
                    <div class="text-2xl font-bold">
                      {{ $t("Message.noContentAvailable") }}
                    </div>
                    <div class="text-neutral-400">
                      {{ $t("Message.thisChapterNoContent") }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-sm md:text-base p-4">
                <story-view
                  :story="currentStory!"
                  :reserve-paragraph-count="2"
                />
                <div class="flex gap-2 w-full justify-center items-center">
                  <div class="h-0 border-t border-t-primary w-[50%]"></div>
                  <div class="shrink-0 text-primary">
                    {{ $t("story.readFullContent") }}
                  </div>
                  <div class="h-0 border-t border-t-primary w-[50%]"></div>
                </div>
              </div>
            </tab>
            <tab :title="$t('story.tab.moment')">
              <div
                v-if="relatedMomentList.length === 0"
                class="flex flex-col items-center justify-center h-[200px]"
              >
                <div class="flex flex-col items-center justify-center gap-2">
                  <div class="text-2xl font-bold">
                    {{ $t("Message.noMomentsYet") }}
                  </div>
                  <div class="text-neutral-400">
                    {{ $t("Message.theCurrentChapter") }}
                  </div>
                </div>
              </div>
              <div v-else class="overflow-y-auto p-4">
                <div
                  v-for="item in relatedMomentList"
                  :key="item.id"
                  class="border-b"
                >
                  <thread-item
                    class="border-b-0"
                    :uuid="item.uuid"
                    :moment="item"
                    :mate-id="author!.uuid"
                    :avatar-url="author!.mediaUrl"
                    :title="author!.name"
                    :time="item.dtCreate"
                    :content="item.text"
                    :mention-matezz="item.mentionMatezz ?? []"
                    :media-list="item.mediazz"
                    :chat-message-list="item.chatMessagezz"
                    :impression-reactions="[]"
                    :type="author!.type"
                    :from-mate="item.fromMate"
                    :mate-tone-id="author!.mateToneId"
                    :enable-audio-play="false"
                    :visibility="item.visibility"
                    :show-comment="false"
                    :show-reaction="false"
                    :enable-reaction-btn="false"
                    :enableTextCollapsed="false"
                    :show-avatar="true"
                    :enable-chat-message-click="false"
                    @thread-click="
                      router.push({
                        name: 'threadDetail',
                        params: { uuid: item.uuid },
                      })
                    "
                  />
                </div>
              </div>
            </tab>
            <tab :title="$t('story.tab.gallery')">
              <div
                class="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-0.5"
                v-if="mediaList.length > 0"
              >
                <div
                  v-for="(url, idx) in mediaList"
                  :key="idx"
                  class="w-full aspect-[1/1]"
                  @click="
                    startPosition = idx;
                    showPreview = true;
                  "
                >
                  <div
                    v-if="isImage(url)"
                    class="bg-base-200 w-full h-full relative"
                  >
                    <Image
                      fit="cover"
                      class="w-full h-full"
                      :src="url"
                      crossorigin="anonymous"
                    />
                  </div>
                  <div v-else class="w-full h-full bg-base-200 relative">
                    <Image
                      fit="cover"
                      class="w-full h-full"
                      :src="url!"
                      crossorigin="anonymous"
                    />
                    <div class="absolute right-1 bottom-1">
                      <i
                        class="iconify mingcute--video-camera-line text-base-content/50"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </tab>
          </tabs>
        </div>
      </div>
    </div>
    <!-- 底部Button Group  -->
    <div
      v-if="currentStory"
      class="flex gap-2 p-4 bg-base-200 border-t border-t-neutral"
    >
      <button
        class="btn btn-sm flex-1"
        v-if="currentStory.dtPublish"
        @click="
          router.push({
            name: 'storyReply',
            params: { uuid: currentStory!.uuid },
          })
        "
      >
        {{ $t("Overview.comments") }}
      </button>
      <button
        class="btn btn-sm flex-1 btn-primary"
        v-show="currentStory!.text"
        @click="
          router.push({
            name: 'readStory',
            params: { uuid: currentStory!.uuid },
          })
        "
      >
        {{ $t("story.read") }}
      </button>
      <button
        v-show="canEdit && currentTab === 0"
        class="btn btn-sm flex-1"
        @click="
          router.push({
            name: 'storyEditor',
            params: { uuid: currentStory!.uuid },
          })
        "
      >
        {{ $t("story.editStory") }}
      </button>
      <button
        v-show="canEdit && currentTab === 1"
        class="btn btn-sm flex-1"
        @click="
          router.push({
            name: 'storyRelatingMoment',
            query: { story: currentStory!.uuid },
          })
        "
      >
        {{ $t("story.base.relation") }}
      </button>
    </div>
    <!-- Story发布Popup  -->
    <story-chapter-publish-popup
      :story="currentStory!"
      v-model:show="showPublishPopup"
    />
    <!-- Story编辑Popup  -->
    <popup
      v-model:show="showEditPopup"
      position="center"
      class="w-[350px] py-10"
      round
      v-if="currentStory"
    >
      <story-info-edit
        ref="storyInfoEditRef"
        :story="currentStory"
        :storyBookId="currentStory.storyBookId"
        :story-id="currentStory.id"
        :title="currentStory.title"
        :description="currentStory.description"
        :media="currentStory.media"
        :video-media="currentStory.videoMedia"
        enable-edit-video
        @on-success="onStoryEditSuccess"
        @on-cancel="showEditPopup = false"
      />
    </popup>
    <mate-media-preview
      v-model:show="showPreview"
      :start-position="startPosition"
      :mate-media-list="previewMediaList"
      :show-media-info="false"
    />
  </div>
</template>
<style>
.story-home .story-view .tiptap {
  padding-bottom: 10px;
}
</style>
